import { FormEventHandler, useEffect, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Flex, Heading4, Icon, InputCheckbox, InputText, Span } from "@goodfill/ui-components";
import { useTranslation } from "react-i18next";
import { FormValidationResult } from "../../../form/formValidation";
import { route } from "../../../routes";
import { signIn } from "@goodfill/backend-api-client";
import { zRequiredString } from "@goodfill/domain-properties-zod-validators";
import { backendFetcher } from "../../backendApi/backendApi";
import { Auth } from "aws-amplify";
import { useRecoilState } from "recoil";
import { userAuthState } from "../../../state";
import { mapLoginDataToUserState } from "../mapLoginDataToUserState";
import { FieldGroup } from "../../../form/FieldGroup";
import { ConfirmationModal } from "../Confirmation/ConfirmationModal";
import { toastErrorHandler } from "../../../unexpectedErrorHandler";
import "../styles/SignInForm.scss";
import { zRequiredEmail } from "../../../form/zod/zodTypes";
import { zodErrorsToString } from "../../../form/zod/zodErrorsToString";
import { useLogout } from "../../useLogout";

interface Props {
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
}

export const SignInForm: React.FC<Props> = ({ isLoading, setIsLoading }) => {
  const [, setUserAuthState] = useRecoilState(userAuthState);
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [username, setUsername] = useState("");
  const [wasUsernameFieldTouched, setWasUserNameFieldTouched] = useState(false);
  const [password, setPassword] = useState("");
  const [wasPasswordFiledTouched, setWasPasswordFieldTouched] = useState(false);

  const [usernameBackendErrors, setUsernameBackendErrors] = useState([] as FormValidationResult);
  const [passwordBackendErrors, setPasswordBackendErrors] = useState([] as FormValidationResult);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);

  const usernameValidation = zRequiredEmail.safeParse(username);
  const passwordValidation = zRequiredString().safeParse(password);

  const [wasInitiallyAutofilled, setWasInitiallyAutofilled] = useState(false);

  // rome-ignore lint/nursery/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setWasInitiallyAutofilled(false);
  }, [username, password]);

  // @see https://github.com/facebook/react/issues/1159#issuecomment-1025423604 for more info
  useLayoutEffect(() => {
    const checkAutofilled = () => {
      const autofilled = !!document.getElementById("email")?.matches("*:-webkit-autofill");
      setWasInitiallyAutofilled(autofilled);
    };
    // The time when it's ready is not very stable, so check few times
    setTimeout(checkAutofilled, 500);
    setTimeout(checkAutofilled, 1000);
    setTimeout(checkAutofilled, 2000);
  }, []);

  const goToSignUp = () => {
    navigate(route.signUp());
  };

  const logout = useLogout();

  const handleLogin = async () => {
    setIsLoading(true);
    setUsernameBackendErrors([]);
    await (
      await signIn(backendFetcher, { username, password })
    ).match({
      success: async (data) => {
        // Todo: remove once we get rid of the Amplify
        try {
          await Auth.signIn({ username, password });
          // By setting the auth state, the user data will be refreshed by effect in SecureRouter
          setUserAuthState(mapLoginDataToUserState(data)); // <---- Leave only this
          // const [value, setValue] = useSessionStorage(userAuthState);
          navigate(route.dashboard()); // <---- Leave only this
        } catch (e: unknown) {
          toastErrorHandler(e);
        }
        // -------------------------------------------
      },
      businessMatcher: {
        notAuthorized: () => {
          setPasswordBackendErrors([t("error.incorrectCredentials")]);
        },
        userNotFound: () => {
          setPasswordBackendErrors([t("error.incorrectCredentials")]);
        },
        userNotConfirmed: () => setIsConfirmationVisible(true),
      },
      unexpected: toastErrorHandler,
      unauthorized: logout,
    });

    setIsLoading(false);
  };

  const goToForgotPassword = () => {
    navigate(route.forgotPassword());
  };

  const onSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    await handleLogin();
  };

  const handleOtpSubmit = async () => {
    setIsLoading(true);

    await (
      await signIn(backendFetcher, { username, password })
    ).match({
      success: async (loginResponse) => {
        // Todo: remove once we get rid of the Amplify
        try {
          await Auth.signIn({ username, password });
          // By setting the auth state, the user data will be refreshed by effect in SecureRouter

          const authData = mapLoginDataToUserState(loginResponse);
          setUserAuthState(authData);
          setIsConfirmationVisible(false);
        } catch (e: unknown) {
          toastErrorHandler(e);
        }
        // -------------------------------------------
      },
      unexpected: toastErrorHandler,
      businessMatcher: {
        userNotConfirmed: toastErrorHandler,
        userNotFound: toastErrorHandler,
        notAuthorized: toastErrorHandler,
      },
      unauthorized: logout,
    });

    setIsLoading(false);
  };

  const submitDisabled =
    !wasInitiallyAutofilled && (isLoading || !usernameValidation.success || !passwordValidation.success);

  return (
    <>
      <ConfirmationModal
        isActive={isConfirmationVisible}
        onSuccess={handleOtpSubmit}
        loginData={{ username, password }}
        isLoading={isLoading}
      />
      <form onSubmit={onSubmit} className="w-full">
        <Flex direction="col" gap={0} width={"w-full"}>
          <Flex direction="col" gap={2}>
            <Heading4>{t("welcome")}</Heading4>
            <Flex className="md-font-big">{t("auth.enter")}</Flex>
          </Flex>

          <FieldGroup
            label={t("email")}
            validation={
              wasUsernameFieldTouched ? [...zodErrorsToString(usernameValidation), ...usernameBackendErrors] : []
            }
            fill
          >
            <InputText
              id="email"
              type="email"
              disabled={isLoading}
              value={username}
              setValue={setUsername}
              border={usernameBackendErrors.length === 0 ? "neutral" : "red"}
              onBlur={() => setWasUserNameFieldTouched(true)}
            />
          </FieldGroup>
          <FieldGroup
            label={t("password")}
            validation={
              wasPasswordFiledTouched ? [...zodErrorsToString(passwordValidation), ...passwordBackendErrors] : []
            }
            fill
            labelRight={
              <Span
                data-testid="forgotPasswordLink"
                id="forgotPasswordLink"
                size="sm"
                selectNone={true}
                onClick={goToForgotPassword}
              >
                <a>{t("auth.forgot?")}</a>
              </Span>
            }
          >
            <InputText
              id="password"
              type="password"
              disabled={isLoading}
              value={password}
              setValue={setPassword}
              border={passwordBackendErrors.length === 0 ? "neutral" : "red"}
              onBlur={() => setWasPasswordFieldTouched(true)}
            />
          </FieldGroup>
          <Flex className="my-6">
            <InputCheckbox onChange={(e) => console.log(e.target.checked)} />
            <p className="ml-3">Remember Me</p>
          </Flex>
          <Flex verticalMargin={4}>
            <Button
              fill
              className="rounded-[6px]"
              data-testid="signInButton"
              id="signInButton"
              primary={true}
              loading={isLoading}
              disabled={submitDisabled}
            >
              {t("auth.signin")}
            </Button>
          </Flex>
          <Flex direction="rowCol" gap={0} justify={"center"} className="login-with mt-6">
            <Span size="sm" selectNone={true} className="login-with-position">
              {t("auth.signInWith")}
            </Span>
          </Flex>
          <Flex gap={2} justify="center" className="mb-10 mt-7">
            <Span className="social-media-icon fb">
              <Icon name="facebook" />
            </Span>
            <Span className="social-media-icon twitter">
              <Icon name="twitter" />
            </Span>
            <Span className="social-media-icon google">
              <Icon name="google" />
            </Span>
            <Span className="social-media-icon apple">
              <Icon name="apple" />
            </Span>
          </Flex>
          <Flex direction="row" gap={0} justify={"center"}>
            <Span size="sm" selectNone={true} className="flex justify-center">
              {t("auth.account?")}
            </Span>
            <Span
              data-testid="signUpLink"
              id="signUpLink"
              primary={true}
              size="sm"
              selectNone={true}
              onClick={goToSignUp}
              className="flex justify-center"
            >
              {t("auth.signup")}
            </Span>
          </Flex>
        </Flex>
      </form>
    </>
  );
};
